import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from './contexts/AuthContext';
import DashboardLayout from './components/DashboardLayout';

const Login       = lazy(() => import('./pages/auth/Login'));
const Register    = lazy(() => import('./pages/auth/Register'));
const Dashboard   = lazy(() => import('./pages/dashboard/Dashboard'));
const Appointments= lazy(() => import('./pages/appointments/Appointments'));
const AppointmentDetail = lazy(() => import('./pages/appointments/AppointmentDetail'));
const Consultation= lazy(() => import('./pages/consultation/Consultation'));
const Patients    = lazy(() => import('./pages/patients/Patients'));
const PatientRecord = lazy(() => import('./pages/patients/PatientRecord'));
const Followup    = lazy(() => import('./pages/followup/Followup'));
const Messages    = lazy(() => import('./pages/messages/Messages'));
const VideoCall   = lazy(() => import('./pages/messages/VideoCall'));
const Settings    = lazy(() => import('./pages/settings/Settings'));

const Loader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin w-8 h-8 border-4 border-sky-600 border-t-transparent rounded-full" />
  </div>
);

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user)   return <Navigate to="/login" replace />;
  return children;
}

function LangWrapper({ children }) {
  const { i18n } = useTranslation();
  React.useEffect(() => {
    const dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <LangWrapper>
        <Toaster position="top-center" toastOptions={{ duration: 3000 }} />
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route path="/login"    element={<Login />} />
            <Route path="/register" element={<Register />} />

            <Route path="/" element={<RequireAuth><DashboardLayout /></RequireAuth>}>
              <Route index                         element={<Dashboard />} />
              <Route path="appointments"           element={<Appointments />} />
              <Route path="appointments/:id"       element={<AppointmentDetail />} />
              <Route path="appointments/:id/consult" element={<Consultation />} />
              <Route path="patients"               element={<Patients />} />
              <Route path="patients/:id"           element={<PatientRecord />} />
              <Route path="followup"               element={<Followup />} />
              <Route path="messages"               element={<Messages />} />
              <Route path="messages/:userId"       element={<Messages />} />
              <Route path="video/:apptId"          element={<VideoCall />} />
              <Route path="settings"               element={<Settings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </LangWrapper>
    </BrowserRouter>
  );
}
